import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Check,
  Clock3,
  X,
  Send,
  Loader2,
  ShieldAlert,
  CheckCircle2,
} from "lucide-react";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Página pública do Ponto de Situação Diário RT (07/02/2026).
 *
 * O consultor recebe um email às 18:30 com um link único
 * `${APP_BASE_URL}/rt-recap/:token`. Aqui justifica cada RT em aberto
 * (Fechado / Pendente / Perdido) + comentário. Ao submeter, o
 * backend envia um email consolidado para rui.pinto@caring.pt.
 */

const OPTIONS = [
  {
    value: "won",
    label: "Fechado",
    hint: "O cliente aceitou a proposta (Ganho)",
    color: "emerald",
    icon: Check,
  },
  {
    value: "pending",
    label: "Ainda pendente",
    hint: "Aguarda decisão do cliente ou informação em falta",
    color: "sky",
    icon: Clock3,
  },
  {
    value: "lost",
    label: "Perdido",
    hint: "O cliente recusou (indica motivo em baixo)",
    color: "rose",
    icon: X,
  },
];

const colorClasses = {
  emerald: {
    ring: "ring-emerald-400",
    bg: "bg-[hsl(var(--success-bg))]",
    text: "text-[hsl(var(--success))]",
    border: "border-[hsl(var(--success))]/40",
    dot: "bg-emerald-500",
  },
  sky: {
    ring: "ring-sky-400",
    bg: "bg-[hsl(var(--info-bg))]",
    text: "text-[hsl(var(--info))]",
    border: "border-[hsl(var(--info))]/40",
    dot: "bg-sky-500",
  },
  rose: {
    ring: "ring-rose-400",
    bg: "bg-[hsl(var(--danger-bg))]",
    text: "text-[hsl(var(--danger))]",
    border: "border-[hsl(var(--danger))]/40",
    dot: "bg-rose-500",
  },
};

function formatDatePt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function PublicRtRecap() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [answers, setAnswers] = useState({}); // { lead_id: {result, comment} }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_ROOT}/public/rt-recap/${token}`);
        setState({ loading: false, data, error: null });
        if (data.already_submitted) setSubmitted(true);
      } catch (e) {
        setState({
          loading: false,
          data: null,
          error: e.response?.data?.detail || "Link inválido ou expirado.",
        });
      }
    })();
  }, [token]);

  const setAnswer = (leadId, patch) => {
    setAnswers((prev) => ({ ...prev, [leadId]: { ...(prev[leadId] || {}), ...patch } }));
  };

  const validation = useMemo(() => {
    if (!state.data?.leads) return { ok: false, missing: [] };
    const missing = [];
    for (const l of state.data.leads) {
      const a = answers[l.lead_id];
      if (!a || !a.result) missing.push({ leadId: l.lead_id, reason: "sem_resultado" });
      else if (!a.comment || !a.comment.trim())
        missing.push({ leadId: l.lead_id, reason: "sem_comentario" });
    }
    return { ok: missing.length === 0, missing };
  }, [answers, state.data]);

  const submit = async () => {
    if (!validation.ok) {
      toast.error("Preenche o resultado e o comentário para cada cliente.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        responses: state.data.leads.map((l) => ({
          lead_id: l.lead_id,
          result: answers[l.lead_id].result,
          comment: answers[l.lead_id].comment.trim(),
        })),
      };
      const { data } = await axios.post(`${API_ROOT}/public/rt-recap/${token}`, payload);
      if (data.already_submitted) {
        toast.info("Respostas já tinham sido enviadas anteriormente.");
      } else if (data.late) {
        toast.warning(
          "Respostas gravadas — mas fora do prazo (19:00). O Rui Pinto foi notificado com essa indicação.",
        );
      } else {
        toast.success("Ponto de situação enviado. O Rui Pinto foi notificado.");
      }
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar. Tenta de novo em segundos.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center">
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
        <div
          className="max-w-md w-full bg-card rounded-lg border border-[hsl(var(--danger))]/25 p-6 text-center"
          data-testid="rt-recap-error"
        >
          <ShieldAlert className="w-10 h-10 mx-auto text-rose-500 mb-3" />
          <h2 className="text-lg font-semibold text-foreground">Link inválido</h2>
          <p className="text-sm text-muted-foreground mt-2">{state.error}</p>
          <p className="text-xs text-muted-foreground mt-4">
            Se recebeste este link por email hoje, contacta a equipa Caring.
          </p>
        </div>
      </div>
    );
  }

  const { data } = state;

  if (submitted) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
        <div
          className="max-w-md w-full bg-card rounded-lg border border-[hsl(var(--success))]/25 p-6 text-center"
          data-testid="rt-recap-submitted"
        >
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
          <h2 className="text-xl font-semibold text-foreground">Obrigado, {data.consultor_first_name}!</h2>
          <p className="text-sm text-muted-foreground mt-2">
            O ponto de situação de {data.leads.length} cliente
            {data.leads.length === 1 ? "" : "s"} foi enviado para o Rui Pinto.
          </p>
          {data.responded_at && (
            <p className="text-xs text-muted-foreground mt-3">
              Registado em {new Date(data.responded_at).toLocaleString("pt-PT")}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-6">
            Podes fechar esta página em segurança.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="max-w-3xl mx-auto p-6 lg:p-10" data-testid="rt-recap-root">
        {/* Header */}
        <div className="mb-8">
          <div className="text-xs font-semibold tracking-widest uppercase text-primary">
            Caring · Ponto de Situação Diário
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold text-foreground mt-2">
            Olá, {data.consultor_first_name || "Consultor"} 👋
          </h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            Tens{" "}
            <strong>
              {data.leads.length} Reuni{data.leads.length === 1 ? "ão Técnica" : "ões Técnicas"}
            </strong>{" "}
            realizada{data.leads.length === 1 ? "" : "s"} onde ainda não indicaste se o cliente
            fechou, está pendente ou foi perdido. Diz-nos como está cada uma —{" "}
            <strong>{data.deadline_label}</strong> é o prazo do dia.
          </p>
          {data.is_past_deadline && (
            <div
              className="mt-4 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] px-4 py-3 text-sm text-[hsl(var(--warning))]"
              data-testid="rt-recap-late-banner"
            >
              <strong>Fora de prazo:</strong> Já passa das 19:00 — pode ainda submeter,
              mas o Rui Pinto vai receber a resposta marcada como "Fora de prazo".
            </div>
          )}
        </div>

        {/* Cards por lead */}
        <div className="space-y-5">
          {data.leads.map((lead, idx) => {
            const a = answers[lead.lead_id] || {};
            return (
              <div
                key={lead.lead_id}
                className="bg-card rounded-xl border border-border shadow-sm p-5 lg:p-6"
                data-testid={`rt-recap-card-${lead.lead_id}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground">
                      Cliente #{idx + 1}
                    </div>
                    <h2 className="text-lg font-semibold text-foreground mt-1 break-words">
                      {lead.client_name}
                    </h2>
                  </div>
                  <div className="text-xs text-muted-foreground text-right">
                    RT em {formatDatePt(lead.rt_completed_at)}
                    {lead.days_open !== null && lead.days_open !== undefined && (
                      <div className="mt-1">
                        <span className="inline-block px-2 py-0.5 rounded bg-muted text-foreground font-semibold">
                          há {lead.days_open} dia{lead.days_open === 1 ? "" : "s"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Radio-buttons customizados */}
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {OPTIONS.map((opt) => {
                    const active = a.result === opt.value;
                    const c = colorClasses[opt.color];
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`rt-recap-option-${lead.lead_id}-${opt.value}`}
                        onClick={() => setAnswer(lead.lead_id, { result: opt.value })}
                        className={`text-left rounded-lg border-2 px-4 py-3 transition-all ${
                          active
                            ? `${c.border} ${c.bg} ring-2 ring-offset-1 ${c.ring}`
                            : "border-border bg-card hover:border-border"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              active ? c.dot : "bg-muted"
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5 text-white" />
                          </span>
                          <span
                            className={`text-sm font-semibold ${
                              active ? c.text : "text-foreground"
                            }`}
                          >
                            {opt.label}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1.5 leading-snug">
                          {opt.hint}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Comentário */}
                <div className="mt-4">
                  <label
                    htmlFor={`comment-${lead.lead_id}`}
                    className="block text-sm font-semibold text-foreground"
                  >
                    Comentário{" "}
                    <span className="font-normal text-muted-foreground">(obrigatório)</span>
                  </label>
                  <textarea
                    id={`comment-${lead.lead_id}`}
                    data-testid={`rt-recap-comment-${lead.lead_id}`}
                    value={a.comment || ""}
                    onChange={(e) =>
                      setAnswer(lead.lead_id, { comment: e.target.value })
                    }
                    placeholder={
                      a.result === "won"
                        ? "Ex.: Cliente aceitou. Início 15/08 com 8h/dia, plano A."
                        : a.result === "lost"
                        ? "Motivo da perda (preço, prazo, decidiu não avançar, etc.)."
                        : "Ex.: Aguarda decisão da filha. Próximo contacto na sexta."
                    }
                    rows={3}
                    maxLength={2000}
                    className="mt-2 block w-full rounded-md border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/20"
                  />
                  <div className="mt-1 text-[11px] text-muted-foreground text-right">
                    {(a.comment || "").length}/2000
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Sticky footer com submit */}
        <div className="mt-8 sticky bottom-4">
          <div className="bg-card/90 backdrop-blur border border-border rounded-xl shadow-lg p-4 flex items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground min-w-0">
              {validation.ok ? (
                <span className="text-[hsl(var(--success))] font-semibold">
                  Tudo pronto para enviar.
                </span>
              ) : (
                <span>
                  Falta{" "}
                  {validation.missing.length === 1
                    ? "1 cliente"
                    : `${validation.missing.length} clientes`}{" "}
                  com resposta completa.
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !validation.ok}
              data-testid="rt-recap-submit-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary hover:bg-primary/90 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> A enviar…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Enviar ponto de situação
                </>
              )}
            </button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6">
          Caring · Cuidados Domiciliários · Este formulário é privado ao consultor.
        </p>
      </div>
    </div>
  );
}
